import { get } from "@puregym/utilities";
import * as ACTIONS from "./constants/actions";

/**
 * For async/complex actions we provide custom functions. We pass `dispatch`
 * from `useReducer`.
 *
 * @see https://reactjs.org/docs/hooks-reference.html#usereducer
 */

/**
 * Try and figure out location based on a search term.
 *
 * @param { Function } dispatch
 * @param { string } searchTerm
 */
export const getLocationGeometry = async (dispatch, searchTerm) => {
  // This should not live here. Improve search panel to handle this locally: don't search again if state hasn't changed
  if (!searchTerm) return;

  dispatch({
    type: ACTIONS.GET_LOCATION,
  });

  try {
    const { ok, body } = await get(
      `${process.env.GATSBY_API_URL}/v1/geolocation`,
      {
        searchquery: searchTerm,
      },
      {
        "Accept-Language": process.env.GATSBY_LOCALE,
      }
    );

    if (!ok) {
      dispatch({
        type: ACTIONS.GET_LOCATION_ERROR,
      });
      return;
    }

    /**
     * A successful (200) response from the API doesn't necessarily mean we
     * found you: the `body.found` bool tells us that.
     */
    if (body.found) {
      dispatch({
        type: ACTIONS.GET_LOCATION_SUCCESS,
        payload: {
          latitude: body.latitude,
          longitude: body.longitude,
          searchTerm: searchTerm,
        },
      });
    } else {
      throw new Error("We don't know where that is!");
    }
  } catch (error) {
    dispatch({
      type: ACTIONS.GET_LOCATION_ERROR,
    });
  }
};

/**
 * Try and get location from the user agent (via `navigator.geolocation`).
 *
 * @param { Function } dispatch - comes from our context, via a hook
 * @param { Object } options - @see https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Geolocation_API/Using_the_Geolocation_API
 * For testing against different locations in Chrome:
 * @see https://developers.google.com/web/tools/chrome-devtools/device-mode/geolocation
 */
export const getGeolocation = async (dispatch, options = {}) => {
  dispatch({
    type: ACTIONS.GET_LOCATION,
  });

  const success = ({ coords: { latitude, longitude } }) =>
    dispatch({
      type: ACTIONS.GET_LOCATION_SUCCESS,
      payload: { latitude, longitude },
    });

  const failure = ({ code }) => {
    const geolocationError = () => {
      switch (code) {
        case 1:
          return "PERMISSION_DENIED";
        case 2:
          return "POSITION_UNAVAILABLE";
        case 3:
          return "TIMEOUT";
        default:
          return "UNKNOWN";
      }
    };

    return dispatch({
      type: ACTIONS.GET_LOCATION_ERROR,
      message: geolocationError(),
    });
  };

  navigator.geolocation.getCurrentPosition(success, failure, options);
};

const asyncActions = { getLocationGeometry, getGeolocation };

export default asyncActions;
